import { requestRearCameraStream, stopCameraStream } from '@dutchie/capacitor-hardware';
import { type ChangeEvent, type FC, useCallback, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';

import {
  FRONT_PHOTO_CAMERA_ACTION,
  FRONT_PHOTO_HEADING,
  FRONT_PHOTO_HINT,
  FRONT_PHOTO_SHUTTER_ACTION,
  FRONT_PHOTO_SKIP_ACTION,
  FRONT_PHOTO_UPLOAD_ACTION,
  frontPhotoCameraErrorMessage,
} from '@dutchie-pos/requests/checkin/camera-id-scan';

import CameraIcon from 'src/assets/camera.svg?react';
import { ImageIcon } from 'src/assets/image-icon';
import { DutchieButton } from 'src/components/dutchie-button';
import { colors } from 'src/css/theme';

import {
  ActionIcon,
  Actions,
  CameraOverlay,
  Container,
  Hint,
  OverlayActions,
  OverlayFrame,
  OverlayVideo,
  Prompt,
} from '../step-chrome';

type FrontPhotoStepProps = {
  isSaving: boolean;
  onCapture: (file: File) => void;
  onSkip: () => void;
};

/** The shutter output. JPEG at this quality is well inside the upload ceiling for a still. */
const SHUTTER_TYPE = 'image/jpeg';
const SHUTTER_QUALITY = 0.9;

// A still, not a scan: no decoder, no region of interest, an explicit shutter. The check-in already happened, so a
// refused camera is not a dead end — Upload and Skip both stay usable.
export const FrontPhotoStep: FC<FrontPhotoStepProps> = ({ isSaving, onCapture, onSkip }) => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isOpening = useRef(false);
  const isMounted = useRef(true);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isOpeningCamera, setIsOpeningCamera] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const releaseStream = useCallback(() => {
    stopCameraStream(streamRef.current);
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsOpeningCamera(false);
  }, []);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      releaseStream();
    };
  }, [releaseStream]);

  useEffect(() => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!isPreviewing || !video || !stream) {
      return;
    }

    video.srcObject = stream;
    void video.play().catch(() => undefined);
  }, [isPreviewing]);

  // Opened from the tap, which iOS Safari requires. The prompt can sit for seconds, so a second tap or
  // a left screen must not strand a live stream.
  const useCamera = async () => {
    if (isOpening.current || streamRef.current) {
      return;
    }

    isOpening.current = true;
    setIsOpeningCamera(true);
    setCameraError(null);
    try {
      const opened = await requestRearCameraStream();

      if (!isMounted.current || streamRef.current) {
        stopCameraStream(opened.stream);
        return;
      }
      if (!opened.stream) {
        setCameraError(frontPhotoCameraErrorMessage(opened.error));
        return;
      }

      streamRef.current = opened.stream;
      setIsPreviewing(true);
    } finally {
      isOpening.current = false;
      if (isMounted.current) {
        setIsOpeningCamera(false);
      }
    }
  };

  const closePreview = () => {
    releaseStream();
    setIsPreviewing(false);
  };

  /** Draws the frame at its own resolution, so nothing is upscaled or cropped. */
  const takePhoto = () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video.videoHeight) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }
    context.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        // The stream stops either way: a failed encode must not leave the camera lit.
        closePreview();
        if (blob) {
          onCapture(new File([blob], 'front.jpg', { type: SHUTTER_TYPE }));
        }
      },
      SHUTTER_TYPE,
      SHUTTER_QUALITY,
    );
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared so picking the same file again re-fires change.
    event.target.value = '';
    if (file) {
      onCapture(file);
    }
  };

  const handleSkip = () => {
    releaseStream();
    setIsPreviewing(false);
    onSkip();
  };

  return (
    <Container>
      <Prompt>{FRONT_PHOTO_HEADING}</Prompt>
      <Hint>{FRONT_PHOTO_HINT}</Hint>
      {cameraError && (
        <CameraError data-testid='check-in_front-photo-error' role='alert'>
          {cameraError}
        </CameraError>
      )}
      <Actions>
        <DutchieButton
          automationId='check-in_front-photo-camera'
          ddActionName='Use camera for front photo (check in)'
          disabled={isSaving || isOpeningCamera}
          onClick={() => {
            useCamera().catch(() => setCameraError(frontPhotoCameraErrorMessage('unknown')));
          }}
        >
          <ActionIcon aria-hidden='true'>
            <CameraIcon />
          </ActionIcon>
          {FRONT_PHOTO_CAMERA_ACTION}
        </DutchieButton>
        <DutchieButton
          automationId='check-in_front-photo-upload'
          ddActionName='Upload front photo (check in)'
          disabled={isSaving}
          secondary
          onClick={() => fileRef.current?.click()}
        >
          <ActionIcon aria-hidden='true'>
            <ImageIcon height={20} width={20} />
          </ActionIcon>
          {FRONT_PHOTO_UPLOAD_ACTION}
        </DutchieButton>
      </Actions>
      <DutchieButton
        automationId='check-in_skip-front-photo'
        ddActionName='Skip front photo (check in)'
        disabled={isSaving}
        secondary
        onClick={handleSkip}
      >
        {isSaving ? 'Saving the photo' : FRONT_PHOTO_SKIP_ACTION}
      </DutchieButton>
      {/* No `capture`: that jumps straight to the camera on iOS and defeats the upload action. */}
      <input
        accept='image/*'
        data-testid='check-in_front-photo-input'
        ref={fileRef}
        style={{ display: 'none' }}
        type='file'
        onChange={handleChange}
      />
      <CameraOverlay $visible={isPreviewing} data-testid='check-in_front-photo-preview'>
        <OverlayFrame>
          <OverlayVideo muted playsInline ref={videoRef} />
        </OverlayFrame>
        {isPreviewing && (
          <OverlayActions>
            <DutchieButton
              automationId='check-in_front-photo-shutter'
              ddActionName='Take front photo (check in)'
              onClick={takePhoto}
            >
              {FRONT_PHOTO_SHUTTER_ACTION}
            </DutchieButton>
            <DutchieButton
              automationId='check-in_front-photo-close'
              ddActionName='Close front photo camera (check in)'
              secondary
              onClick={closePreview}
            >
              Cancel
            </DutchieButton>
          </OverlayActions>
        )}
      </CameraOverlay>
    </Container>
  );
};

const CameraError = styled.p`
  color: ${colors.red};
  font-size: 0.875rem;
  line-height: 1.25rem;
  margin: 0;
`;
